/**
 * DRBFM Results Step Component
 *
 * Orchestrates the DRBFM analysis results view:
 * - Summary statistics (ResultsSummaryCards)
 * - Header actions (ResultsHeader)
 * - CAD link fallback (CadLinkPrompt + useCadLinking)
 * - Design changes / failure modes / countermeasures tables (ResultsTabs)
 * - Convert / drawing-evidence / part-label dialogs
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useConfirm } from '@hooks/useConfirm';
import {
  DesignChange,
  FailureMode,
  FailureModeRevisionPreview,
  PredefinedPartLabel,
  SessionResultsResponse,
  UpdateDesignChangeReviewRequest,
  UpdateFailureModeReviewRequest,
} from '../../../types';
import ConvertToVerificationDialog from '../ConvertToVerificationDialog';
import CADPartLabelEditorDialog from '../../cad/CADPartLabelEditorDialog';
import DRBFMDrawingEvidenceDialog from '../DRBFMDrawingEvidenceDialog';
import ResultsHeader from './results/ResultsHeader';
import ResultsSummaryCards from './results/ResultsSummaryCards';
import CadLinkPrompt from './results/CadLinkPrompt';
import ResultsTabs from './results/ResultsTabs';
import { useCadLinking } from './results/useCadLinking';

interface DRBFMResultsStepProps {
  results: SessionResultsResponse | null;
  onExport: () => void;
  onPreview?: () => void;
  onNewAnalysis: () => void;
  isExporting: boolean;
  exportError: string | null;
  drbfmSessionId?: string;
  drbfmItemName?: string;
  onLinkCad?: (cadModelId: string, predefinedPartLabels?: PredefinedPartLabel[]) => Promise<void>;
  isLinkingCad?: boolean;
  linkCadError?: string | null;
  cadEnabled?: boolean | null;
  onReviewDesignChange?: (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => Promise<void>;
  onReviewFailureMode?: (
    failureModeId: string,
    request: UpdateFailureModeReviewRequest,
  ) => Promise<void>;
  onPreviewFailureModeRevision?: (
    failureModeId: string,
    instruction: string,
  ) => Promise<FailureModeRevisionPreview>;
}

const DRBFMResultsStep: React.FC<DRBFMResultsStepProps> = ({
  results,
  onExport,
  onPreview,
  onNewAnalysis,
  isExporting,
  exportError,
  drbfmSessionId,
  drbfmItemName,
  onLinkCad,
  isLinkingCad = false,
  linkCadError = null,
  cadEnabled = null,
  onReviewDesignChange,
  onReviewFailureMode,
  onPreviewFailureModeRevision,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(results?.failureModes?.length ? 1 : 0);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedFailureModeId, setSelectedFailureModeId] = useState<string | undefined>();
  const [isDecisionPanelOpen, setIsDecisionPanelOpen] = useState(false);
  const [autoSelectedTabSessionId, setAutoSelectedTabSessionId] = useState<string | undefined>(
    () => results?.session?.id,
  );
  const [drawingEvidenceFailureMode, setDrawingEvidenceFailureMode] = useState<FailureMode | null>(
    null,
  );
  const [reviewingArtifactId, setReviewingArtifactId] = useState<string | undefined>();
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [showExcludedHistory, setShowExcludedHistory] = useState(false);

  const { visibleResults, excludedResults, excludedCount } = useMemo(() => {
    if (!results) {
      return { visibleResults: null, excludedResults: null, excludedCount: 0 };
    }
    const excluded = (status?: string) => status === 'rejected' || status === 'superseded';
    const designChanges = results.designChanges.filter((change) => !excluded(change.reviewStatus));
    const designChangeIds = new Set(designChanges.map((change) => change.id));
    const failureModes = results.failureModes.filter((mode) => (
      !excluded(mode.reviewStatus)
      && (!mode.designChangeId || designChangeIds.has(mode.designChangeId))
    ));
    const failureModeIds = new Set(failureModes.map((mode) => mode.id));
    const countermeasures = results.countermeasures.filter((measure) => (
      !excluded(measure.reviewStatus)
      && (!measure.failureModeId || failureModeIds.has(measure.failureModeId))
    ));
    const visibleDesignChangeIds = new Set(designChanges.map((change) => change.id));
    const visibleFailureModeIds = new Set(failureModes.map((mode) => mode.id));
    const visibleCountermeasureIds = new Set(countermeasures.map((measure) => measure.id));
    const hiddenDesignChanges = results.designChanges.filter(
      (change) => !visibleDesignChangeIds.has(change.id),
    );
    const hiddenFailureModes = results.failureModes.filter(
      (mode) => !visibleFailureModeIds.has(mode.id),
    );
    const hiddenCountermeasures = results.countermeasures.filter(
      (measure) => !visibleCountermeasureIds.has(measure.id),
    );
    return {
      visibleResults: { ...results, designChanges, failureModes, countermeasures },
      excludedResults: {
        ...results,
        designChanges: hiddenDesignChanges,
        failureModes: hiddenFailureModes,
        countermeasures: hiddenCountermeasures,
      },
      excludedCount:
        hiddenDesignChanges.length + hiddenFailureModes.length + hiddenCountermeasures.length,
    };
  }, [results]);
  const historyMode = showExcludedHistory && excludedCount > 0;
  const displayedResults = historyMode ? excludedResults : visibleResults;

  const handleToggleHistory = () => {
    const nextResults = historyMode ? visibleResults : excludedResults;
    const hasReviewableDesignChange = nextResults?.designChanges.some(
      (change) => change.reviewStatus === 'rejected',
    );
    const hasReviewableFailureMode = nextResults?.failureModes.some(
      (mode) => mode.reviewStatus === 'rejected',
    );
    if (!historyMode && hasReviewableDesignChange) {
      setTabValue(0);
    } else if (!historyMode && hasReviewableFailureMode) {
      setTabValue(1);
    } else if (nextResults?.designChanges.length) {
      setTabValue(0);
    } else if (nextResults?.failureModes.length) {
      setTabValue(1);
    } else if (nextResults?.countermeasures.length) {
      setTabValue(2);
    }
    setShowExcludedHistory(!historyMode);
  };

  const {
    selectedCadModelId,
    partLabelsJson,
    partLabelsError,
    partLabelEditorOpen,
    editorInitialLabels,
    handleLinkCad,
    handleCadModelChange,
    handleOpenPartLabelEditor,
    handleApplyPartLabels,
    handlePartLabelsJsonChange,
    closePartLabelEditor,
  } = useCadLinking(onLinkCad);

  const selectedFailureMode = useMemo(
    () => displayedResults?.failureModes?.find(
      (failureMode) => failureMode.id === selectedFailureModeId,
    ),
    [displayedResults?.failureModes, selectedFailureModeId],
  );
  const selectedDesignChange = useMemo<DesignChange | undefined>(
    () => results?.designChanges.find(
      (change) => change.id === selectedFailureMode?.designChangeId,
    ),
    [results?.designChanges, selectedFailureMode?.designChangeId],
  );

  // Auto-open the failure-modes tab once per session — adjusted during render
  // (React's "a prop changed" pattern) rather than in an effect, so there is no
  // stale-tab paint. Self-terminates once this session id is recorded.
  if (
    visibleResults?.session.id
    && visibleResults.failureModes?.length
    && autoSelectedTabSessionId !== visibleResults.session.id
  ) {
    setAutoSelectedTabSessionId(visibleResults.session.id);
    setTabValue(1);
  }

  useEffect(() => {
    const currentSelectionStillExists = displayedResults?.failureModes?.some(
      (failureMode) => failureMode.id === selectedFailureModeId,
    );
    if (!currentSelectionStillExists) {
      setSelectedFailureModeId(undefined);
      setIsDecisionPanelOpen(false);
    }
  }, [displayedResults?.failureModes, selectedFailureModeId]);

  // 3D bulk viewer button — derive cad_model_id from any anchored failure mode.
  // The button is hidden when no failure mode carries a CAD anchor (TG #673).
  const anchoredCadModelId = visibleResults?.failureModes?.find((fm) => fm.cadAnchor)?.cadAnchor
    ?.cadModelId;
  const unlinkedFailureModesCount = visibleResults?.failureModes?.filter(
    (fm) => !fm.cadAnchor,
  ).length || 0;
  const canViewAll3D = Boolean(anchoredCadModelId && drbfmSessionId);
  const canOfferLinkCad = cadEnabled === true && Boolean(
    drbfmSessionId && onLinkCad && unlinkedFailureModesCount > 0,
  );

  const handleView3DAll = () => {
    if (!canViewAll3D) return;
    const params = new URLSearchParams();
    params.set('cad_model', anchoredCadModelId!);
    params.set('drbfm_session', drbfmSessionId!);
    navigate(`/cad-viewer?${params.toString()}`);
  };

  const handleFailureModeSelect = (failureMode: FailureMode) => {
    setSelectedFailureModeId(failureMode.id);
    setIsDecisionPanelOpen(true);
  };

  const handleReviewDesignChange = async (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => {
    if (!onReviewDesignChange) return;
    if (request.reviewStatus === 'rejected') {
      const relatedFailureModes = visibleResults?.failureModes.filter(
        (mode) => mode.designChangeId === designChangeId,
      ) || [];
      const relatedFailureModeIds = new Set(relatedFailureModes.map((mode) => mode.id));
      const relatedCountermeasures = visibleResults?.countermeasures.filter(
        (measure) => measure.failureModeId && relatedFailureModeIds.has(measure.failureModeId),
      ) || [];
      const confirmed = await confirm({
        title: t(
          'drbfm.decisionMemory.rejectDesignChangeTitle',
          '変更点を非採用にしますか？',
        ),
        description: t('drbfm.decisionMemory.rejectDesignChangeDescription', {
          failureModes: relatedFailureModes.length,
          countermeasures: relatedCountermeasures.length,
          defaultValue: `この変更点を非採用にすると、関連する故障モード ${relatedFailureModes.length}件と対策 ${relatedCountermeasures.length}件も現在の結果から除外されます。判断履歴から再検討に戻せます。`,
        }),
        confirmLabel: t('drbfm.decisionMemory.confirmReject', '非採用にする'),
      });
      if (!confirmed) return;
    }
    setReviewingArtifactId(designChangeId);
    setReviewError(null);
    try {
      await onReviewDesignChange(designChangeId, request);
      if (request.reviewStatus === 'ai_candidate') {
        setShowExcludedHistory(false);
      }
    } catch (error) {
      setReviewError(t('drbfm.decisionMemory.reviewFailed', '設計判断を保存できませんでした。'));
      throw error;
    } finally {
      setReviewingArtifactId(undefined);
    }
  };

  const handleReviewFailureMode = async (
    failureModeId: string,
    request: UpdateFailureModeReviewRequest,
  ) => {
    if (!onReviewFailureMode) return;
    if (request.reviewStatus === 'rejected') {
      const relatedCountermeasures = visibleResults?.countermeasures.filter(
        (measure) => measure.failureModeId === failureModeId,
      ) || [];
      const confirmed = await confirm({
        title: t(
          'drbfm.decisionMemory.rejectFailureModeTitle',
          '故障モードを非採用にしますか？',
        ),
        description: t('drbfm.decisionMemory.rejectFailureModeDescription', {
          countermeasures: relatedCountermeasures.length,
          defaultValue: `この故障モードを非採用にすると、関連する対策 ${relatedCountermeasures.length}件も現在の結果から除外されます。判断履歴から再検討して採用できます。`,
        }),
        confirmLabel: t('drbfm.decisionMemory.confirmReject', '非採用にする'),
      });
      if (!confirmed) return;
    }
    setReviewingArtifactId(failureModeId);
    setReviewError(null);
    try {
      await onReviewFailureMode(failureModeId, request);
      if (historyMode && ['accepted', 'modified'].includes(request.reviewStatus)) {
        setShowExcludedHistory(false);
      }
    } catch (error) {
      setReviewError(t('drbfm.decisionMemory.reviewFailed', '設計判断を保存できませんでした。'));
      throw error;
    } finally {
      setReviewingArtifactId(undefined);
    }
  };

  const handlePreviewFailureModeRevision = async (
    failureModeId: string,
    instruction: string,
  ) => {
    if (!onPreviewFailureModeRevision) {
      throw new Error('revision preview unavailable');
    }
    setReviewingArtifactId(failureModeId);
    setReviewError(null);
    try {
      return await onPreviewFailureModeRevision(failureModeId, instruction);
    } finally {
      setReviewingArtifactId(undefined);
    }
  };

  // No results state
  if (!results) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-semibold text-muted-foreground mb-2">
          {t('drbfm.results.noResults')}
        </h3>
        <Button className="mt-4" onClick={onNewAnalysis}>
          <RefreshCw />
          {t('drbfm.results.startNew')}
        </Button>
      </div>
    );
  }

  // Calculate statistics
  const designChangesCount = visibleResults?.designChanges?.length || 0;
  // Domain thresholds for the summary counts.
  const CRITICAL_SEVERITY_SCORE = 8;
  const HIGH_EFFECTIVENESS_SCORE = 8;
  const TOP_PRIORITY = 1;

  const failureModesCount = visibleResults?.failureModes?.length || 0;
  const countermeasuresCount = visibleResults?.countermeasures?.length || 0;

  // Count critical failure modes
  const criticalCount = visibleResults?.failureModes?.filter(
    (fm) => fm.severity === 'critical' || fm.severityScore >= CRITICAL_SEVERITY_SCORE
  ).length || 0;

  // Count high priority countermeasures
  const highPriorityCount = visibleResults?.countermeasures?.filter(
    (cm) => cm.priority === TOP_PRIORITY || cm.effectivenessScore >= HIGH_EFFECTIVENESS_SCORE
  ).length || 0;

  return (
    <div>
      <ResultsHeader
        onNewAnalysis={onNewAnalysis}
        onOpenConvertDialog={() => setConvertDialogOpen(true)}
        onPreview={onPreview}
        canViewAll3D={canViewAll3D}
        onView3DAll={handleView3DAll}
        onExport={onExport}
        isExporting={isExporting}
      />

      {/* Export Error */}
      {exportError && (
        <Alert variant="destructive" className="mb-4">
          <CircleAlert />
          <AlertDescription>{exportError}</AlertDescription>
        </Alert>
      )}

      {linkCadError && (
        <Alert variant="destructive" className="mb-4">
          <CircleAlert />
          <AlertDescription>{linkCadError}</AlertDescription>
        </Alert>
      )}

      {reviewError && (
        <Alert variant="destructive" className="mb-4">
          <CircleAlert />
          <AlertDescription>{reviewError}</AlertDescription>
        </Alert>
      )}

      {canOfferLinkCad && (
        <CadLinkPrompt
          anchoredCadModelId={anchoredCadModelId}
          unlinkedFailureModesCount={unlinkedFailureModesCount}
          selectedCadModelId={selectedCadModelId}
          onSelectCadModel={handleCadModelChange}
          onOpenPartLabelEditor={handleOpenPartLabelEditor}
          partLabelsJson={partLabelsJson}
          onPartLabelsJsonChange={handlePartLabelsJsonChange}
          partLabelsError={partLabelsError}
          onLinkCad={handleLinkCad}
          isLinkingCad={isLinkingCad}
        />
      )}

      <ResultsSummaryCards
        designChangesCount={designChangesCount}
        failureModesCount={failureModesCount}
        countermeasuresCount={countermeasuresCount}
        criticalCount={criticalCount}
        highPriorityCount={highPriorityCount}
      />

      <ResultsTabs
        results={displayedResults || results}
        tabValue={tabValue}
        onTabChange={setTabValue}
        drbfmSessionId={drbfmSessionId}
        selectedFailureMode={selectedFailureMode}
        selectedDesignChange={selectedDesignChange}
        onSelectFailureMode={handleFailureModeSelect}
        isDecisionPanelOpen={isDecisionPanelOpen}
        onCloseDecisionPanel={() => setIsDecisionPanelOpen(false)}
        onOpenDrawingEvidence={setDrawingEvidenceFailureMode}
        reviewingArtifactId={reviewingArtifactId}
        excludedCount={excludedCount}
        historyMode={historyMode}
        onToggleHistory={handleToggleHistory}
        onReviewDesignChange={onReviewDesignChange ? handleReviewDesignChange : undefined}
        onReviewFailureMode={onReviewFailureMode ? handleReviewFailureMode : undefined}
        onPreviewFailureModeRevision={onPreviewFailureModeRevision
          ? handlePreviewFailureModeRevision
          : undefined}
      />

      {/* Convert to Verification Dialog */}
      {drbfmSessionId && results && (
        <ConvertToVerificationDialog
          open={convertDialogOpen}
          onClose={() => setConvertDialogOpen(false)}
          drbfmSessionId={drbfmSessionId}
          drbfmItemName={drbfmItemName || ''}
          failureModes={visibleResults?.failureModes || []}
          designChanges={visibleResults?.designChanges || []}
          countermeasures={visibleResults?.countermeasures || []}
        />
      )}
      <DRBFMDrawingEvidenceDialog
        open={Boolean(drawingEvidenceFailureMode)}
        failureMode={drawingEvidenceFailureMode}
        onClose={() => setDrawingEvidenceFailureMode(null)}
      />
      <CADPartLabelEditorDialog
        open={partLabelEditorOpen}
        cadModelId={selectedCadModelId}
        initialLabels={editorInitialLabels}
        onApply={handleApplyPartLabels}
        onClose={closePartLabelEditor}
      />
    </div>
  );
};

export default DRBFMResultsStep;
